import { Injectable } from '@angular/core';
import { Tool } from '@trimble-agentic-external-npm-local/agentic-platform-sdk-iframe-typescript';
import { MapPanAndZoomStream } from 'src/app/feature/map-viewer/common/map-pan-zoom-stream.service';
import { SidePanelStreamsService } from 'src/app/feature/map-viewer/side-panel/side-panel-streams.service';
import { SidePanelName } from 'src/app/feature/map-viewer/side-panel/side-panel.component';
import { FeatureService } from 'src/app/shared/map-data-services/feature/feature.service';
import { MapService } from 'src/app/feature/map-viewer/map.service';
import { FullFeatureFilter } from '../../feature-filter/full-feature-filter';
import { FeaturesStreamsService } from '../../current-features/features-streams.service';
import { ProjectStreamService } from '../../current-project/project-stream.service';

type SelectAction = 'replace' | 'add' | 'remove' | 'clear';

@Injectable({ providedIn: 'root' })
export class SelectFeaturesTool {
    constructor(
        private featuresStreams: FeaturesStreamsService,
        private featureService: FeatureService,
        private mapPanAndZoomStream: MapPanAndZoomStream,
        private mapService: MapService,
        private sidePanelStreams: SidePanelStreamsService,
        private projectStream: ProjectStreamService
    ) {}

    readonly definition: Tool = {
        name: 'select_features',
        description:
            'Select, add to selection, remove from selection, or clear selected features on the map and optionally zoom to them. ' +
            'Use this when the user asks to select, unselect, highlight, or focus on specific features.',
        parameters: {
            type: 'object',
            properties: {
                feature_ids: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'The IDs of the features to select or unselect. Not required when action is "clear".'
                },
                action: {
                    type: 'string',
                    enum: ['replace', 'add', 'remove', 'clear'],
                    description:
                        'The selection action: "replace" replaces current selection, "add" adds to current selection, ' +
                        '"remove" removes from current selection, "clear" clears all selected features. Defaults to "replace".'
                },
                zoom_to_features: {
                    type: 'boolean',
                    description: 'Whether to zoom the map to show the selected features. Defaults to true.'
                }
            },
            required: ['feature_ids']
        }
    };

    async execute(params: {
        feature_ids: string[];
        action?: SelectAction;
        zoom_to_features?: boolean;
    }): Promise<string> {
        try {
            const { feature_ids = [], action = 'replace', zoom_to_features = true } = params;

            if (action === 'clear') {
                this.featuresStreams.clearSelectedFeatures();
                this.sidePanelStreams.closeSidePanel(SidePanelName.SELECTED_FEATURES);
                return JSON.stringify({ status: 'ok', message: 'Selection cleared' });
            }

            if (!feature_ids?.length) {
                return JSON.stringify({ status: 'error', message: 'No feature IDs provided' });
            }

            await this.applySelection(feature_ids, action);

            if (feature_ids.length) {
                this.sidePanelStreams.openSidePanel(SidePanelName.SELECTED_FEATURES);
            }

            if (zoom_to_features && feature_ids.length) {
                await this.zoomToFeatures(feature_ids);
            }

            return JSON.stringify({
                status: 'ok',
                action,
                featureCount: feature_ids.length,
                zoomedToFeatures: zoom_to_features
            });
        } catch (error) {
            return JSON.stringify({ status: 'error', message: 'Failed to select features' });
        }
    }

    private applySelection(featureIds: string[], action: SelectAction): Promise<void> {
        return new Promise<void>(resolve => {
            const callback = () => resolve();
            switch (action) {
                case 'replace':
                    this.featuresStreams.replaceOnSelectedFeatures(featureIds, callback);
                    break;
                case 'add':
                    this.featuresStreams.addToSelectedFeatures(featureIds, callback);
                    break;
                case 'remove':
                    this.featuresStreams.removeFromSelectedFeatures(featureIds, callback);
                    break;
                default:
                    resolve();
            }
        });
    }

    private async zoomToFeatures(featureIds: string[]): Promise<void> {
        const projectId = this.projectStream.getCurrentProject()?.id;
        if (!projectId) return;

        const filter = new FullFeatureFilter();
        filter.selectedFeatureIds = featureIds;

        const bounds = await this.featureService.getBoundsOfFeatures(projectId, filter);
        if (!bounds?.isValid()) return;

        const basemapMode = this.mapService.selectedBaseMapMode;
        this.mapPanAndZoomStream.zoomToBounds(bounds, [360, 60], [340, 10], {
            maxZoom: basemapMode?.maxZoom
        });
    }
}
